import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminGetMetrics } from "../lib/callables";
import { PAYMENTS_ENABLED } from "../lib/payments";
import { licensedApps, type AdminMetrics } from "@acruxsoft/platform-shared";

export function MetricsBar() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<AdminMetrics | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminGetMetrics({});
        if (!cancelled) setData(data);
      } catch (e) {
        console.error("[metrics-bar] load failed", e);
        if (!cancelled) setErr(t("adminPanel.metrics.loadError"));
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  if (err) return <div className="acx__alert acx__alert--warning" role="alert">{err}</div>;
  if (!data) return <div className="acx__metrics-row acx__metrics-row--loading" aria-busy="true" />;

  const fmtMrr = formatCurrency(data.mrr.amount, data.mrr.currency, i18n.language);

  return (
    <div className="acx__metrics-row">
      <MetricCard label={t("adminPanel.metrics.totalUsers")} value={String(data.totalUsers)} />
      {licensedApps().map((app) => (
        <MetricCard
          key={app.appId}
          label={t("adminPanel.metrics.activeLicenses", { app: t(`home.apps.${app.appId}.shortName`) })}
          value={String(data.activeLicenses[app.appId] ?? 0)}
          accent="paid"
        />
      ))}
      <MetricCard
        label={t("adminPanel.metrics.expiringIn30d")}
        value={String(data.licensesExpiringIn30d)}
        accent={data.licensesExpiringIn30d > 0 ? "warning" : undefined}
      />
      <Link to="/admin/requests" className="acx__metric-link">
        <MetricCard
          label={t("adminPanel.metrics.pendingRequests")}
          value={String(data.pendingLicenseRequests)}
          sub={t("adminPanel.metrics.pendingRequestsSub")}
          accent={data.pendingLicenseRequests > 0 ? "primary" : undefined}
        />
      </Link>
      {PAYMENTS_ENABLED && <PaypalMetrics data={data} fmtMrr={fmtMrr} />}
    </div>
  );
}

/** The PayPal side of the dashboard — shown only while payments are on. */
function PaypalMetrics({ data, fmtMrr }: { data: AdminMetrics; fmtMrr: string }) {
  const { t } = useTranslation();
  return (
    <>
      <MetricCard
        label={t("adminPanel.metrics.activePaid")}
        value={String(data.activePaid)}
        sub={data.pastDue > 0 ? t("adminPanel.metrics.pastDue", { count: data.pastDue }) : undefined}
        accent="paid"
      />
      <MetricCard
        label={t("adminPanel.metrics.activeManual")}
        value={String(data.activeManual)}
        accent="manual"
      />
      <MetricCard
        label={t("adminPanel.metrics.mrr")}
        value={fmtMrr}
        sub={t("adminPanel.metrics.mrrSub")}
        accent="primary"
      />
      <MetricCard
        label={t("adminPanel.metrics.cancelledLast30d")}
        value={String(data.cancelledLast30d)}
        accent={data.cancelledLast30d > 0 ? "warning" : undefined}
      />
    </>
  );
}

function MetricCard({
  label,
  value,
  sub,
  accent
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: "paid" | "manual" | "primary" | "warning";
}) {
  const cls = "acx__metric" + (accent ? ` acx__metric--${accent}` : "");
  return (
    <div className={cls}>
      <span className="acx__metric-label">{label}</span>
      <span className="acx__metric-value">{value}</span>
      {sub && <span className="acx__metric-sub">{sub}</span>}
    </div>
  );
}

function formatCurrency(amount: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
